import ClearIcon from '@mui/icons-material/Close'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ChangeEvent, useCallback } from 'react'
import { connectField, filterDOMProps } from 'uniforms'
import { Markdown } from '../../Markdown'
import { useCiboAutoForm } from './CiboAutoForm'
import { ValidationMessages } from './ValidationMessages'

export interface ClearableNumberFieldProps {
  value: number | undefined | null
  onChange: (value: number | undefined) => void
  label?: string
  disabled?: boolean
  error?: any
  helperText?: string
  required?: boolean
  name: string
  placeholder?: string
  [key: string]: any // uniforms injects many props
}

export function ClearableNumberInput({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
  required,
  placeholder,
  description,
  ...props
}: ClearableNumberFieldProps) {
  const uiParams = props['ui-params'] || {}

  const { touch } = useCiboAutoForm()

  const handleZeroValueCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange(0)
      } else {
        onChange(undefined)
      }
      touch(props.name)
    },
    [onChange, props.name, touch]
  )

  return (
    <>
      <FormControl fullWidth {...filterDOMProps(props)} required={required} disabled={disabled}>
        {label && <FormLabel component="legend">{label}</FormLabel>}

        <TextField
          {...filterDOMProps(props)}
          type="number"
          value={value ?? ''}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          helperText={helperText}
          onChange={e => {
            const v = e.target.value
            // Empty string → undefined (Uniforms best practice)
            if (v === '') onChange(undefined)
            else onChange(Number(v))

            touch(props.name)
          }}
          onBlur={() => touch(props.name)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>{uiParams.units || ''}</Typography>

                  <Tooltip title={'Clear value'}>
                    <IconButton
                      size="small"
                      onClick={() => onChange(undefined)}
                      disabled={disabled || !value}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
          fullWidth
        />

        <ValidationMessages fieldName={props.name} error={error} />

        {!!description && (
          <Markdown typographyProps={{ variant: 'caption' }}>{description}</Markdown>
        )}
      </FormControl>

      {uiParams.zeroValueCta && (
        <FormControlLabel
          control={<Checkbox checked={value === 0} onChange={handleZeroValueCheckbox} />}
          label={uiParams.zeroValueCta}
        />
      )}
    </>
  )
}

export const NumberField = connectField(ClearableNumberInput)
